import React from 'react';
import * as S from './DealRoomInsuranceCard.styles';
import { IDealRoomDocument, IDealRoomSection } from '@app/interfaces/deal-room';
import { useTranslation } from 'react-i18next';
import { decryptField } from '@app/utils/utils';
import { Tooltip } from 'antd';
import { IUser } from '@app/interfaces/user';
import { MF_LABELS, mfElementLabel, mfTag, mfTrackAction, mfTrackSection } from '@app/utils/mouseflowTracker';

interface DealRoomInsuranceCardProps {
  user: IUser | null;
  mainOffer: IDealRoomDocument[];
  section: IDealRoomSection;
}

export const DealRoomInsuranceCard: React.FC<DealRoomInsuranceCardProps> = ({ user, mainOffer, section }) => {
  const { t } = useTranslation();

  const openLinkInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // The Hebrew label customers see for this section — fall back to section.label
  // if the deal admin overrode it, otherwise use the translated default key.
  const sectionLabel: string = section.label || t('dealRooms.sections.decidedToPurchaseTheInsurance') || '';

  const handleFormClick = (offer: IDealRoomDocument) => {
    // High-value conversion event: customer is leaving for the digital purchase
    // form. Emit a section pageview with the Hebrew label and the dedicated
    // Hebrew apply tag so the funnel analyzer picks this up as the apply step.
    mfTrackSection(sectionLabel);
    mfTag(`${MF_LABELS.applyFormClick}:${offer?.name || offer?.link || 'הצעה'}`);
    openLinkInNewTab(offer.link || '');
  };

  // Container-level click capture catches any other interactive elements added
  // to the insurance card in the future (since this card lives outside
  // DealRoomLanding's S.DealContent delegated listener).
  const onContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const interactive = target.closest<HTMLElement>('a[href], button, [role="button"], [data-mf-track]');
    if (!interactive) return;
    // The form button has its own explicit tracking — skip the duplicate tag.
    if (interactive.closest('[data-mf-skip-delegate="true"]')) return;
    mfTrackAction(MF_LABELS.sectionClickPrefix, sectionLabel, mfElementLabel(interactive));
  };

  return (
    <>
      <div id="decided-to-purchase-the-insurance" onClickCapture={onContainerClick}>
        {mainOffer.map((offer: IDealRoomDocument, index: number) => (
          <Tooltip key={index} title={offer.description}>
            <S.StyledCard>
              <S.Content>
                <S.Title>
                  {section.label ? section.label : t('dealRooms.sections.decidedToPurchaseTheInsurance')}
                </S.Title>
                {offer?.name && <S.SubTitle>{offer.label ? offer.label : offer?.name}</S.SubTitle>}
                <S.Description>אנא הכנס לטופס ומלא את הפרטים הנדרשים.</S.Description>
              </S.Content>
              <S.ButtonContainer>
                <Tooltip title={user ? t('dealRooms.sections.representativeNotAuthorizedToOpenRoomForm') : ''}>
                  <S.StyledButton data-mf-skip-delegate="true" onClick={() => handleFormClick(offer)} disabled={!!user}>
                    מעבר לטופס
                  </S.StyledButton>
                </Tooltip>
                {offer?.digitalFormPassword && (
                  <S.Password>
                    {t('dealRooms.landing.password')}:{' '}
                    <S.PasswordText>{decryptField(offer?.digitalFormPassword)}</S.PasswordText>
                  </S.Password>
                )}
              </S.ButtonContainer>
            </S.StyledCard>
          </Tooltip>
        ))}
      </div>
    </>
  );
};
